import { useEffect, useState } from 'react'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { useEquivalentDomains } from '@features/equivalent-domains/hooks/useEquivalentDomains'
import type { EquivalentDomainGroupWire } from '@features/equivalent-domains/api/equivalentDomainsApi'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Settings § domain: Equivalent domain groups の編集 (T-G6、Phase 5)。
 *
 * 「同じパスワードを使うドメイン」を group 化する。
 * `preferences.mergeDomains=true` 時、useVaultList が同 group 内 domain を 1 行に統合表示する。
 */

interface GroupEditorState {
  id: string | null // null = new group
  name: string
  domainsRaw: string // 改行 / カンマ区切り
}

const parseDomains = (raw: string): string[] => {
  return raw
    .split(/[\n,]/u)
    .map(d => d.trim().toLowerCase())
    .filter(d => d !== '')
}

const groupToEditor = (g: EquivalentDomainGroupWire): GroupEditorState => ({
  id: g.id,
  name: g.name,
  domainsRaw: g.domains.join('\n'),
})

const newGroupEditor = (): GroupEditorState => ({ id: null, name: '', domainsRaw: '' })

export const DomainSection = (): JSX.Element => {
  const { t } = useTranslation()
  const { state, lastErrorKey, create, update, remove } = useEquivalentDomains()
  const [editor, setEditor] = useState<GroupEditorState | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    // state 変化で active editor を維持しつつ、削除済 row の編集 panel を閉じる
    if (editor === null || editor.id === null) return
    if (state.kind !== 'ready') return
    if (!state.groups.some(g => g.id === editor.id)) {
      setEditor(null)
    }
  }, [state, editor])

  const onSave = async (): Promise<void> => {
    if (editor === null || submitting) return
    const domains = parseDomains(editor.domainsRaw)
    if (domains.length === 0 || editor.name.trim() === '') return
    setSubmitting(true)
    try {
      if (editor.id === null) {
        await create({ name: editor.name.trim(), domains })
      } else {
        await update(editor.id, { name: editor.name.trim(), domains })
      }
      setEditor(null)
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (id: string): Promise<void> => {
    if (submitting) return
    setSubmitting(true)
    try {
      await remove(id)
    } finally {
      setSubmitting(false)
    }
  }

  if (state.kind === 'loading') {
    return <div className="settings-item-sub">{t('settings.domain.loading')}</div>
  }

  return (
    <div>
      <Notice tone="info" ico="info">
        {t('settings.domain.help')}
      </Notice>

      {lastErrorKey !== null && (
        <Notice tone="danger" ico="alert">
          {t(lastErrorKey)}
        </Notice>
      )}

      {state.kind === 'ready' && state.groups.length === 0 && editor === null && (
        <EmptyState
          icon="lock"
          title={t('settings.domain.empty_title')}
          sub={t('settings.domain.empty_sub')}
        />
      )}

      {state.kind === 'ready' &&
        state.groups.map(g => (
          <div
            key={g.id}
            className="card settings-domain-group"
            data-testid={`domain-group-${g.id}`}
          >
            <div className="card-row-flex">
              <div>
                <div className="settings-item-label">{g.name}</div>
                <div className="settings-item-sub">{g.domains.join(', ')}</div>
              </div>
              <div className="settings-emergency-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditor(groupToEditor(g))}
                  disabled={submitting}
                >
                  {t('settings.domain.edit')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void onDelete(g.id)}
                  disabled={submitting}
                  data-testid={`domain-delete-${g.id}`}
                >
                  {t('settings.domain.delete')}
                </button>
              </div>
            </div>
          </div>
        ))}

      {editor !== null && (
        <div className="card settings-form-card" data-testid="domain-editor">
          <div className="settings-card-title">
            {editor.id === null ? t('settings.domain.new_title') : t('settings.domain.edit_title')}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="domain-group-name">
              {t('settings.domain.name_label')}
            </label>
            <input
              id="domain-group-name"
              className="input"
              type="text"
              maxLength={80}
              value={editor.name}
              onChange={ev => setEditor({ ...editor, name: ev.target.value })}
              required
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="domain-group-domains">
              {t('settings.domain.domains_label')}
            </label>
            <textarea
              id="domain-group-domains"
              className="textarea settings-domain-textarea"
              rows={5}
              placeholder={t('settings.domain.placeholder')}
              value={editor.domainsRaw}
              onChange={ev => setEditor({ ...editor, domainsRaw: ev.target.value })}
            />
            <div className="settings-avatar-hint">{t('settings.domain.domains_hint')}</div>
          </div>
          <div className="settings-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEditor(null)}
              disabled={submitting}
            >
              {t('settings.domain.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onSave()}
              disabled={
                submitting ||
                editor.name.trim() === '' ||
                parseDomains(editor.domainsRaw).length === 0
              }
              data-testid="domain-save"
            >
              {submitting ? t('settings.domain.saving') : t('settings.domain.save')}
            </button>
          </div>
        </div>
      )}

      {editor === null && (
        <div className="settings-form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setEditor(newGroupEditor())}
            disabled={submitting}
            data-testid="domain-add"
          >
            <Icon name="plus" aria-hidden="true" />
            {t('settings.domain.add')}
          </button>
        </div>
      )}
    </div>
  )
}
